// frontend/packages/business-console/src/components/build-interview/JourneyRail.jsx
//
// Desktop "journey rail" for the build interview (Website Builder Redesign,
// Phase 1): the 10 steps as a vertical list with a connector line, per-step
// icon badges, and done / current / upcoming states. Completed steps are
// clickable to jump back and edit (reuses the existing editingStepId path).
//
// Driven entirely by the server step list (`steps[].{id,order,title,done}`) +
// the active step id. Colors via useTokens() so light/dark is automatic.

import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import useTokens from '../primitives/useTokens';
import { stepMeta } from './stepMeta';

function StepBadge({ state, Icon, tokens }) {
  const base = {
    width: 30,
    height: 30,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    zIndex: 1,
    transition: 'all .15s ease',
  };
  if (state === 'done') {
    return (
      <Box sx={{ ...base, background: tokens.gradient }}>
        <CheckIcon sx={{ color: '#fff', fontSize: 17 }} />
      </Box>
    );
  }
  if (state === 'current') {
    return (
      <Box
        data-bi-anim
        sx={{
          ...base,
          background: tokens.card,
          border: `2px solid ${tokens.blue}`,
          animation: 'bi-ringpulse 1.8s ease-out infinite',
        }}
      >
        <Icon sx={{ color: tokens.blue, fontSize: 15 }} />
      </Box>
    );
  }
  return (
    <Box sx={{ ...base, background: tokens.grayBg, border: `1px solid ${tokens.grayBorder}` }}>
      <Icon sx={{ color: tokens.faint, fontSize: 15 }} />
    </Box>
  );
}

export default function JourneyRail({
  steps = [],
  activeId = null,
  busy = false,
  completed = false,
  onSelect,
}) {
  const t = useTokens();
  return (
    <Box
      sx={{
        width: 234,
        flexShrink: 0,
        alignSelf: 'flex-start',
        background: t.card,
        border: `1px solid ${t.cardBorder}`,
        borderRadius: '16px',
        boxShadow: t.cardShadowSoft,
        p: '16px 14px',
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: t.faint,
          mb: 1,
          pl: 0.5,
        }}
      >
        Your steps
      </Typography>

      <Box sx={{ position: 'relative' }}>
        {/* connector line behind the badges */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 15,
            bottom: 15,
            left: 19,
            width: 2,
            background: t.hairline,
          }}
        />
        {steps.map((s) => {
          const isDone = !!s.done;
          const isActive = s.id === activeId;
          const state = isDone ? 'done' : isActive ? 'current' : 'upcoming';
          const Icon = stepMeta(s.id).icon;
          const clickable = isDone && !completed && !busy;
          return (
            <Box
              key={s.id}
              onClick={clickable ? () => onSelect && onSelect(s.id) : undefined}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: '5px 6px',
                borderRadius: '10px',
                cursor: clickable ? 'pointer' : 'default',
                background:
                  isActive && !isDone ? 'rgba(37,99,235,.07)' : 'transparent',
                '&:hover': clickable
                  ? { background: 'rgba(37,99,235,.06)' }
                  : undefined,
              }}
            >
              <StepBadge state={state} Icon={Icon} tokens={t} />
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: state === 'current' ? 800 : 600,
                  color:
                    state === 'done'
                      ? t.textSecondary
                      : state === 'current'
                        ? t.blue
                        : t.faint,
                  // Wrap long step titles to two lines instead of truncating
                  // mid-word (WS-F A4).
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.3,
                }}
              >
                {s.order}. {s.title}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
